import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { SkeletonModule } from 'primeng/skeleton';

import {
   HighRiskHotelReportItem,
   HighRiskHotelRowVm
} from '../../../domain/corporate/high-risk-hotel-row-vm';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import type { Property as TyallsProperty } from '../../../domain/property';
import { PropertyStoreService } from '../../../services/stores/property-store.service';

import { TaskUtils } from '../../../util/task-utils';
import type { Task } from '../../../domain/task';

type HotelRiskThresholds = {
   highMinAmount: number;
   mediumMinAmount: number;
   mediumMaxAmount: number;
   lowMaxAmount: number;
};

const DEFAULT_HOTEL_RISK_THRESHOLDS: HotelRiskThresholds = {
   highMinAmount: 500,
   mediumMinAmount: 151,
   mediumMaxAmount: 499,
   lowMaxAmount: 150
};

const isDefined = <T>(v: T | null | undefined): v is T => v != null;
@Component({
   selector: 'app-high-risk-hotels-table',
   standalone: true,
   imports: [
      CommonModule,
      TableModule,
      ButtonModule,
      TagModule,
      CurrencyPipe,
      SkeletonModule
   ],
   templateUrl: './high-risk-hotels-table.component.html',
   styleUrls: ['./high-risk-hotels-table.component.scss']
})
export class HighRiskHotelsTableComponent implements OnChanges {
   @Input() rows: HighRiskHotelRowVm[] = [];
   @Input() loading: boolean = false;
   @Input() daysInRange: number = 0;
   @Input() propertyById: Record<number, TyallsProperty> = {};

   readonly thresholds = DEFAULT_HOTEL_RISK_THRESHOLDS;

   expandedRowKeys: Record<string, boolean> = {};

   constructor(
      private router: Router,
      private propStore: PropertyStoreService
   ) {}

   ngOnChanges(changes: SimpleChanges): void {
      if (changes['rows']) {
         this.expandedRowKeys = {};
      }
   }
   onRowExpand(event: any): void {
      const row: HighRiskHotelRowVm | undefined = event?.data;
      const key = row?.propertyId;
      if (key == null) return;
      this.expandedRowKeys = { ...this.expandedRowKeys, [String(key)]: true };
   }

   onRowCollapse(event: any): void {
      const row: HighRiskHotelRowVm | undefined = event?.data;
      const key = row?.propertyId;
      if (key == null) return;
      const next = { ...this.expandedRowKeys };
      delete next[String(key)];
      this.expandedRowKeys = next;
   }

   riskClass(status: HighRiskHotelRowVm['status']): string {
      return status === 'High'
         ? 'risk-high'
         : status === 'Medium'
           ? 'risk-medium'
           : 'risk-low';
   }

   avgDailyImpact(row: HighRiskHotelRowVm): number {
      const total = row.financialImpact ?? 0;
      const days = this.daysInRange ?? 0;
      if (days <= 0) return 0;
      return total / days;
   }

   trackByReport(_i: number, r: HighRiskHotelReportItem): any {
      return r.taskInstanceId ?? `${r.date}-${r.reportName}-${r.amount}-${_i}`;
   }

   async goToTaskFromReport(
      hotelRow: HighRiskHotelRowVm,
      report: HighRiskHotelReportItem,
      $event: MouseEvent
   ): Promise<void> {
      $event.preventDefault();
      $event.stopPropagation();

      const taskInstanceId = report?.taskInstanceId;
      if (!taskInstanceId) return;

      // 1) Prefer correct property for that row (corporate context)
      let property: TyallsProperty | undefined =
         this.propertyById?.[hotelRow.propertyId];

      // 2) Fallback (only if lookup not provided): use currentProperty
      if (!property) {
         property = await firstValueFrom(
            this.propStore.currentProperty.pipe(filter(isDefined), take(1))
         );
      }

      // 3) Minimal "Task" object (only processId needed by TaskUtils.goToTask)
      // If TS complains here, see note below.
      const taskRef = { processId: taskInstanceId } as Task;

      await TaskUtils.goToTask(taskRef, this.router, property);
   }
}
